import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

export interface YoutubeVideo {
  videoId: string;
  title: string;
  thumbnail: string;
}

export interface YoutubePlaylist {
  title: string;
  videos: YoutubeVideo[];
}

const CACHE_KEYS = {
  videos: 'yt_cache_videos',
  title:  'yt_cache_title',
  time:   'yt_cache_time',
};
const CACHE_TTL = 24 * 60 * 60; // secondes

@Injectable({ providedIn: 'root' })
export class YoutubePlaylistService {

  constructor(private http: HttpClient) {}

  getPlaylist(): Observable<YoutubePlaylist> {
    if (this.isCacheValid()) {
      return of(this.readCache());
    }

    const apiKey    = environment.youtube?.apiKey;
    const playlistId = this.extractPlaylistId(environment.youtube?.playlistId);

    if (!apiKey || !playlistId) {
      return of({ title: '', videos: [] });
    }

    const baseUrl = 'https://www.googleapis.com/youtube/v3';

    const title$  = this.http.get<any>(
      `${baseUrl}/playlists?part=snippet&id=${playlistId}&key=${apiKey}`
    ).pipe(
      map(r => (r.items?.[0]?.snippet?.title as string) || ''),
      catchError(() => of(''))
    );

    const videos$ = this.http.get<any>(
      `${baseUrl}/playlistItems?part=snippet&playlistId=${playlistId}&maxResults=20&key=${apiKey}`
    ).pipe(
      map(r => ((r.items || []) as any[])
        .map((item: any) => ({
          videoId:   item.snippet?.resourceId?.videoId || '',
          title:     item.snippet?.title || '',
          thumbnail: item.snippet?.thumbnails?.medium?.url
                  || item.snippet?.thumbnails?.default?.url
                  || '',
        }))
        .filter((v: YoutubeVideo) => !!v.videoId)
      ),
      catchError(() => of([] as YoutubeVideo[]))
    );

    return forkJoin({ title: title$, videos: videos$ }).pipe(
      tap(({ title, videos }) => {
        if (videos.length > 0) this.writeCache(title, videos);
      })
    );
  }

  private extractPlaylistId(raw: string | undefined): string {
    if (!raw) return '';
    try {
      const url = new URL(raw);
      return url.searchParams.get('list') || raw;
    } catch {
      return raw;
    }
  }

  private isCacheValid(): boolean {
    const ts = localStorage.getItem(CACHE_KEYS.time);
    if (!ts) return false;
    return (Date.now() / 1000 - parseInt(ts, 10)) < CACHE_TTL;
  }

  private readCache(): YoutubePlaylist {
    return {
      title:  localStorage.getItem(CACHE_KEYS.title) || '',
      videos: JSON.parse(localStorage.getItem(CACHE_KEYS.videos) || '[]'),
    };
  }

  private writeCache(title: string, videos: YoutubeVideo[]): void {
    localStorage.setItem(CACHE_KEYS.videos, JSON.stringify(videos));
    if (title) localStorage.setItem(CACHE_KEYS.title, title);
    localStorage.setItem(CACHE_KEYS.time, Math.floor(Date.now() / 1000).toString());
  }
}
